import { CarbonCredit } from 'src/app/shared/interfaces/carbon-credit';
import { CarbonCreditConsultation } from 'src/app/shared/interfaces/consultation-credit.model';
import { Pagination } from 'src/app/shared/interfaces/pagination';
import { resolvePsaCertifierDescription } from 'src/app/helpers/utils/psa-certifier.util';
import { CarbonFilter } from '../interfaces/carbon-filter';

export interface AppTableColumn {
  title: string;
  contentType?: string;
  id: string;
  orderable?: boolean;
  sortedDirectionBy?: 'ASC' | 'DESC' | string;
  statusBadge?: any;
  valuePrepareFunction?: any;
  actions?: Array<{
    icon?: string;
    tooltip?: string;
    onClick?: (row: any) => void;
  }>;
}

export enum OrderTable {
  ASC = 'ASC',
  DESC = 'DESC',
}

export function createCharacteristicConsultPagination(
  size = 10,
): Pagination<CarbonCredit> {
  return {
    page: 1,
    size,
    results: [{}] as CarbonCredit[],
  };
}

function stringColumn(title: string, id: string, orderable = false): AppTableColumn {
  return { title, contentType: 'string', id, orderable };
}

export function buildCharacteristicConsultColumns(
  onOpenDetail: (row: CarbonCredit) => void,
): AppTableColumn[] {
  const stringColumns: Array<[string, string]> = [
    ['Código IF', 'carbonCreditCode'],
    ['ID do Projeto', 'projectId'],
    ['Serial Range', 'serialCode'],
    ['Conta do Registrador', 'registerCode'],
    ['Conta Detentor', 'holderCode'],
  ];

  return [
    {
      ...stringColumn('Data de Registro', 'insertDate', true),
      sortedDirectionBy: OrderTable.DESC,
    },
    ...stringColumns.map(([title, id]) => stringColumn(title, id)),
    {
      title: 'Status do Registro',
      contentType: 'badge',
      id: 'statusCodeDescription',
      orderable: false,
      statusBadge: resolveStatusBadgeClass,
    },
    {
      title: 'Detalhes',
      contentType: 'action',
      id: 'actions',
      orderable: false,
      actions: [
        { icon: 'open_in_new', onClick: (row) => onOpenDetail(row), tooltip: 'Detalhes' },
      ],
    },
  ];
}

export function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (value instanceof Date) return isNaN(value.getTime());
  return false;
}

export function hasValidDateRange(filters: CarbonFilter): boolean {
  if (isEmptyValue(filters.initialDate) && isEmptyValue(filters.finalDate)) {
    return true;
  }
  return !isEmptyValue(filters.initialDate) && !isEmptyValue(filters.finalDate);
}

export function hasActiveDateRange(filters: CarbonFilter): boolean {
  return !isEmptyValue(filters.initialDate) && !isEmptyValue(filters.finalDate);
}

export function toConsultApiFilter(filters: CarbonFilter): CarbonFilter {
  return {
    carbonCreditCode: filters.carbonCreditCode,
    initialDate: filters.initialDate,
    finalDate: filters.finalDate,
    registerCode: filters.registerCode,
    holderCode: filters.holderCode,
    filterByInsertDate: hasActiveDateRange(filters),
  };
}

export function extractPrintResults(
  response: unknown,
): Array<{ statusCode?: string }> {
  if (Array.isArray(response)) {
    return response;
  }

  if (response && typeof response === 'object') {
    const apiResponse = response as { data?: Record<string, unknown> };

    if (apiResponse.data) {
      const approvedInstruments = apiResponse.data['approvedInstruments'];
      if (Array.isArray(approvedInstruments)) {
        return approvedInstruments;
      }
    }

    const paginated = response as Pagination<CarbonCredit>;
    if (Array.isArray(paginated.results)) {
      return paginated.results;
    }
  }

  return [];
}

export function flattenCarbonCreditConsultationRow(harvest: CarbonCreditConsultation) {
  return {
    DATA_DE_REGISTRO: harvest.issueDate,
    CODIGO_CONTA_REGISTRADOR: harvest.registerCode,
    CODIGO_CONTA_DETENTOR: harvest.holderCode,
    CODIGO_DE_INST_FINANCEIRO: harvest.carbonCreditCode,
    ID_DO_PROJETO: harvest.projectId,
    STATUS: harvest.statusCode,
    TIPO_DE_INSTRUMENTO_FINANCEIRO: harvest.financialInstrumentTypeCode,
    DATA_DE_EMISSAO: harvest.insertDate,
    QUANTIDADE_EMITIDA: harvest.creditInitialQuantity,
    QUANTIDADE_ATUAL: harvest.creditQuantity,
    DESCRICAO_ADICIONAL: harvest.observationText,
    INICIO_DA_SAFRA: harvest.vintageStart,
    FINAL_DA_SAFRA: harvest.vintageEnd,
    NOME_DO_PROJETO: harvest.projectName,
    SERIAL_RANGE_CREDITS_ID: harvest.serialCode,
    LINK_DO_PROJETO_NA_CERTIFICADORA: harvest.projectLink,
    NOME_DA_CERTIFICADORA: resolvePsaCertifierDescription(
      harvest.serialCode,
      harvest.certifierDescription,
    ),
    INFORMAÇÕES_ADICIONAIS: harvest.commentStatus,
  };
}

export function countActiveFilters(filters: CarbonFilter): number {
  let count = 0;

  if (hasActiveDateRange(filters)) {
    count++;
  }

  if (!isEmptyValue(filters.registerCode)) count++;
  if (!isEmptyValue(filters.holderCode)) count++;
  if (!isEmptyValue(filters.carbonCreditCode)) count++;

  return count;
}

export function resolveStatusBadgeClass(statusDescription: string): string {
  const value = (statusDescription || '').toUpperCase();

  if (value.includes('REJEITADO PELA ACX')) return 'badge-danger';
  if (value.includes('REJEITADO PELA B3') || value.includes('REJEITADO POR OPERA')) return 'badge-danger';
  if (value.includes('REJEITADO PELO CLIENTE')) return 'badge-warning';
  if (value.includes('AGUARDANDO CONFIRMAÇÃO')) return 'badge-warning';
  if (value.includes('AGUARDANDO VALIDAÇÃO') || value.includes('VALIDAÇÃO DA ACX')) return 'badge-info';
  if (value.includes('AGUARDANDO') || value.includes('PENDENTE')) return 'badge-warning';
  if (value.includes('CONFIRMADO PELO CLIENTE')) return 'badge-primary';
  if (value.includes('REGISTRADO NA B3') || value.includes('APROVADO')) return 'badge-success';

  return 'badge-primary';
}
